import { useEffect, useState } from "react";

import { ArrowButton, Button, DatePicker, Modal, TextField, Typography } from "@king-ict/eupisi-ui/components";
import { useValidation } from "@king-ict/eupisi-ui/hooks";
import { nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router";

import { createEnrollmentCondition, editEnrollmentCondition, getEnrollmentConditionById } from "../../api/nisooApi";
import { useInput } from "../../hooks";
import { EnrollmentCondition, EnrollmentConditionValidationScheme } from "../../models/EnrollmentCondition";
import { codebookRouteEnrollmentCondition } from "../../routes/routes";
import styles from "./RequestCodeBook.module.scss";

export default function CodeBookEnrollmentConditionEdit(): JSX.Element {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const { handleChange } = useInput();


  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [enrollmentCondition, setEnrollmentCondition] = useState<EnrollmentCondition>({ name: "", canDelete: true });

  useEffect(() => {
    if (!location || !location.state || !(location.state as any).enrollmentConditionId) {
      setLoading(false);
      return;
    }
    const getEnrollmentCondition = async () => {
      var result = await getEnrollmentConditionById((location.state as any).enrollmentConditionId);
      setEnrollmentCondition(result);
      setLoading(false);
    }
    getEnrollmentCondition();
  }, [location]);


  const [errors, doValidations] = useValidation({
    data: enrollmentCondition,
    validationSchema: EnrollmentConditionValidationScheme,
  });

  const saveChanges = () => {
    var isValid = doValidations();
    if (isValid) {
      setOpenConfirmModal(true);
    }
  }

  const handleCancelConfirmModal = async () => {
    setOpenConfirmModal(false);
  }

  const handleConfirmedConfirmModal = async () => {
    setOpenConfirmModal(false);
    if (enrollmentCondition.dateTo) {
      enrollmentCondition.dateTo = new Date(Date.UTC(enrollmentCondition.dateTo.getFullYear(), enrollmentCondition.dateTo.getMonth(), enrollmentCondition.dateTo.getDate()));
    }

    if (enrollmentCondition.id) {
      await editEnrollmentCondition(enrollmentCondition.id, enrollmentCondition);
    }
    else {
      await createEnrollmentCondition(enrollmentCondition);
    }
    navigateToList();
  }

  const cancel = () => {
    navigateToList();
  }

  const navigateToList = () => {
    history.push(
      codebookRouteEnrollmentCondition(),
      {
        page: "list"
      }
    );
  }

  return (
    <>
      <Row>
        <Col md={9}>
          <ArrowButton backwards ariaLabel="" onClick={navigateToList} />
          <span className={styles.backButtonCaption}>
            {t("codeBook.returnToList")}
          </span>
        </Col>
      </Row>
      <Row className="mt-5">
        <Col md={8}>
          <h3>{t("codeBook.enrollmentCondition")}</h3>
        </Col>
        <Col md={4} className="d-flex justify-content-end align-items-center">
          <Button outline onClick={() => cancel()}>
            {t("common.cancel")}
          </Button>
          <Button className="ml-3" onClick={() => saveChanges()}>
            {t("common.save")}
          </Button>
        </Col>
      </Row>
      {!loading &&
        <Row className="mt-5">
          <Col md={8}>
            <TextField
              name={nameof<typeof enrollmentCondition>("name")}
              label={t("common.name")}
              errorText={errors?.name}
              value={enrollmentCondition.name}
              maxLength={256}
              onChange={handleChange(setEnrollmentCondition)}
            />
          </Col>
          <Col md={4} >
            <DatePicker
              value={enrollmentCondition.dateTo}
              label={t("common.dateTo")}
              name={nameof<typeof enrollmentCondition>("dateTo")}
              onChange={handleChange(setEnrollmentCondition)}
              isClearable={true}
              minYear={new Date().getFullYear() - 25}
              maxYear={new Date().getFullYear() + 25}
            />
          </Col>
        </Row>
      }
      <Modal
        type="confirm"
        open={openConfirmModal}
        cancelText={t("common.cancel")}
        confirmText={t("common.saveChanges")}
        onClose={handleCancelConfirmModal}
        onConfirm={handleConfirmedConfirmModal}
      >
        <div>
          <Typography component="h1" variant="largeBold">
            {t("common.areYouSureSaveChanges")}
          </Typography>
        </div>
      </Modal>
    </>
  );
}